/**
 * Figma Node IDs:
 * Design Frame: 1493:72731
 * Instance IDs: I1493:71779, I1493:71850, I1493:71928, I1493:71949, I1493:71970, I1493:71991
 * Composite: true
 * Last Updated: 2024-12-22
 * Generated by: Claude Code figma-component command
 */
import React, { useId, useState } from "react";
import { SingleSelectQuestionProps } from "./types";
import { defineMessages, useIntl } from "react-intl";
import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";
import { fetchTranslations } from "./locales/config";
import { RadioButtonGroup } from "./RadioButtonGroup";
import { Figure } from "../Figure";
import { Collapsible } from "../LxCollapsible";
import { LxButton } from "../LxButton";
import { FeedbackBanner } from "../FeedbackBanner";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import {
  singleSelectQuestionClass,
  containerClass,
  questionCategoryClass,
  questionTextClass,
  questionTextParagraphClass,
  optionsSectionClass,
  buttonSlabClass,
  attemptsTextClass,
  predictionFeedbackWrapperClass,
  collapsibleClass,
} from "./SingleSelectQuestion.css";

/* ---------------------------- Internationalization ---------------------------- */

export const messages = defineMessages({
  attemptsLeft: {
    id: "Question.attemptsLeft",
    defaultMessage: "Attempts left: {attemptsLeft}",
    description:
      "Label showing the number of attempts left for the question. {attemptsLeft} is a placeholder for the actual number.",
  },
  submitAnswer: {
    id: "Question.submitAnswer",
    defaultMessage: "Submit answer",
    description: "Text for the button to submit the selected answer.",
  },
  submitPrediction: {
    id: "Question.submitPrediction",
    defaultMessage: "Submit prediction",
    description: "Text for the button to submit the selected prediction.",
  },
  resetQuestion: {
    id: "Question.reset",
    defaultMessage: "Reset question",
    description: "Text for the button to reset the question and try again.",
  },
  resetSelection: {
    id: "Question.predictionReset",
    defaultMessage: "Reset prediction",
    description: "Text for the button to reset the question and try again.",
  },
  resetQuestionAriaLabel: {
    id: "Question.resetQuestionAriaLabel",
    defaultMessage: "Reset question button",
    description:
      "Aria label for the button to reset the question and try again.",
  },
  resetPredictionAriaLabel: {
    id: "Question.resetPredictionAriaLabel",
    defaultMessage: "Reset prediction button",
    description:
      "Aria label for the button to reset the prediction and try again.",
  },
  predictionSubmitted: {
    id: "Question.predictionSubmitted",
    defaultMessage:
      "<b>Prediction submitted.</b> You will compare this answer with your final result at the end of the experiment.",
    description:
      "Text shown in the banner when a prediction question has been submitted.",
  },
  singleSelectHeader: {
    id: "Question.singleSelectHeader",
    defaultMessage: "Single-select Question",
    description: "Header text for single-select question type.",
  },
  predictionHeader: {
    id: "Question.predictionHeader",
    defaultMessage: "Prediction Question",
    description: "Header text for prediction question type.",
  },
});

/* ---------------------------- UI Subcomponents ---------------------------- */

const QuestionHeader: React.FC<{
  id: string;
  isPrediction: boolean;
}> = ({ id, isPrediction }) => (
  <p id={id} className={questionCategoryClass}>
    {isPrediction ? (
      <RichText value={messages.predictionHeader} />
    ) : (
      <RichText value={messages.singleSelectHeader} />
    )}
  </p>
);

const QuestionFooter: React.FC<{
  attemptsLeft: number;
  showReset: boolean;
  onSubmit: () => void;
  onReset: () => void;
  isSubmitDisabled: boolean;
  isPredictionQuestion: boolean;
}> = ({
  attemptsLeft,
  showReset,
  onSubmit,
  onReset,
  isSubmitDisabled,
  isPredictionQuestion,
}) => {
  const { formatMessage } = useIntl();
  const submitText = formatMessage(
    isPredictionQuestion ? messages.submitPrediction : messages.submitAnswer
  );
  const resetText = formatMessage(
    isPredictionQuestion ? messages.resetSelection : messages.resetQuestion
  );
  const resetAriaLabel = formatMessage(
    isPredictionQuestion
      ? messages.resetPredictionAriaLabel
      : messages.resetQuestionAriaLabel
  );
  const submitButtonType = isPredictionQuestion ? "alternative" : "primary";

  return (
    <div className={buttonSlabClass} data-name="Button slab">
      {attemptsLeft > -1 && (
        <p
          className={attemptsTextClass}
          data-testid="attempts-left"
          aria-live="polite"
          role="status"
        >
          <RichText
            value={{
              ...messages.attemptsLeft,
              placeholderValues: { attemptsLeft },
            }}
          />
        </p>
      )}
      {showReset ? (
        <LxButton
          variant="secondary"
          text={resetText}
          ariaLabel={resetAriaLabel}
          size="large"
          onClickHandler={onReset}
        />
      ) : (
        <LxButton
          variant={submitButtonType}
          text={submitText}
          size="large"
          onClickHandler={onSubmit}
          disabled={isSubmitDisabled}
        />
      )}
    </div>
  );
};

/* ---------------------------- Main Component ---------------------------- */

const SingleSelectQuestionInternal: React.FC<SingleSelectQuestionProps> = ({
  content,
  question,
  text,
  image,
  options,
  answerId,
  hintDetails,
  isPredictionQuestion = false,
  selectedOptionIndex,
  onOptionSelect,
  onSubmit,
  onReset,
  setCompleted,
  totalAttempts = -1, // -1 means unlimited
}) => {
  const [attemptsLeft, setAttemptsLeft] = useState<number>(totalAttempts);
  const [submittedOptionIndexes, setSubmittedOptionIndexes] = useState<
    number[]
  >([]);
  const [resetCount, setResetCount] = useState(0); // to force re-mounts

  // Generate single unique ID for this component instance
  const componentId = useId();
  const headerId = `${componentId}-header`;
  const textId = `${componentId}-text`;
  const questionId = `${componentId}-question`;
  const imageId = `${componentId}-image`;
  const imageCaptionId = `${componentId}-caption`;

  /* ---------------------------- Derived Booleans ---------------------------- */

  const hasSubmittedAnswers = submittedOptionIndexes.length > 0;

  const hasCorrectAnswer = submittedOptionIndexes.some((idx) =>
    answerId.includes(idx)
  );

  const hasWrongAnswer = submittedOptionIndexes.some(
    (idx) => !answerId.includes(idx)
  );

  const showResetButton = isPredictionQuestion
    ? hasSubmittedAnswers
    : hasSubmittedAnswers && hasCorrectAnswer;

  const showPredictionFeedback = isPredictionQuestion && hasSubmittedAnswers;

  const isSubmitDisabled =
    selectedOptionIndex === undefined ||
    (!isPredictionQuestion &&
      hasWrongAnswer &&
      submittedOptionIndexes.includes(selectedOptionIndex) &&
      !hasCorrectAnswer);

  const isOptionSelectionDisabled =
    (hasCorrectAnswer && !isPredictionQuestion) ||
    (isPredictionQuestion && hasSubmittedAnswers) ||
    attemptsLeft === 0;

  /* ---------------------------- Handlers ---------------------------- */

  const handleSubmit = () => {
    if (selectedOptionIndex !== undefined) {
      const isCorrectAnswer = answerId.includes(selectedOptionIndex);
      const willRunOutOfAttempts = attemptsLeft === 1; // will be 0 after decrement

      // Determine if question will be completed after this submission
      const willBeCompleted =
        isCorrectAnswer || // correct answer completes the question
        willRunOutOfAttempts || // no more attempts left
        isPredictionQuestion; // prediction questions complete after any submission

      setAttemptsLeft((prev) => {
        if (prev > 0) return prev - 1;
        if (prev === -1) return -1; // unlimited mode
        return 0;
      });

      setSubmittedOptionIndexes((prev) => [...prev, selectedOptionIndex]);

      // Call setCompleted after updating state
      if (willBeCompleted) {
        setCompleted?.(true);
      }

      onSubmit();
    }
  };

  const handleReset = () => {
    setSubmittedOptionIndexes([]);
    setAttemptsLeft(totalAttempts);
    setResetCount((prev) => prev + 1);
    if (onReset) onReset();
  };

  /* ---------------------------- Dynamic Content ---------------------------- */

  // Build aria-labelledby string for RadioButtonGroup
  // Include header, text (if exists), question, image alt text (if exists), and image caption (if exists)
  const buildAriaLabelledby = () => {
    const ids: string[] = [headerId];
    if (text) ids.push(textId);
    ids.push(questionId);
    if (image) ids.push(imageId);
    if (image?.caption) ids.push(imageCaptionId);
    return ids.join(" ");
  };

  const ariaLabelledby = buildAriaLabelledby();

  const renderContent = () => {
    const elements: React.ReactNode[] = [];

    // Always render header first
    elements.push(
      <QuestionHeader
        key="header"
        id={headerId}
        isPrediction={isPredictionQuestion}
      />
    );

    // Then render content in the order specified
    content.forEach((type, index) => {
      switch (type) {
        case "text":
          if (text) {
            elements.push(
              <div
                id={textId}
                key={`text-${index}`}
                className={questionTextClass}
              >
                <p className={questionTextParagraphClass}>
                  <RichText value={text} />
                </p>
              </div>
            );
          }
          break;
        case "question":
          elements.push(
            <div
              id={questionId}
              key={`q-${index}`}
              className={questionTextClass}
            >
              <p className={questionTextParagraphClass}>
                <RichText value={question} />
              </p>
            </div>
          );
          break;
        case "image":
          if (image) {
            elements.push(
              <Figure
                key={`img-${index}`}
                src={image.src}
                altText={image.altText}
                caption={image.caption}
                imageId={imageId}
                captionId={image.caption ? imageCaptionId : undefined}
              />
            );
          }
          break;
        case "options":
          elements.push(
            <div
              key={`opt-${index}`}
              className={optionsSectionClass}
              data-name="Options"
            >
              <RadioButtonGroup
                options={options}
                selectedOptionIndex={selectedOptionIndex}
                submittedOptionIndexes={submittedOptionIndexes}
                answerId={answerId}
                onOptionSelect={
                  isOptionSelectionDisabled
                    ? () => {
                        /* no-op */
                      }
                    : onOptionSelect || (() => {})
                }
                ariaLabelledby={ariaLabelledby}
                showFeedbackOnSubmit={!isPredictionQuestion}
                resetCount={resetCount}
              />
            </div>
          );
          break;
        case "hintDetails":
          if (hintDetails) {
            elements.push(
              <div className={collapsibleClass}>
                <Collapsible
                  key={`hint-${index}`}
                  label={hintDetails.label}
                  content={hintDetails.content}
                  open={hintDetails.open}
                />
              </div>
            );
          }
          break;
        default:
          break;
      }
    });

    return elements;
  };

  /* ---------------------------- Render ---------------------------- */

  return (
    <div
      className={singleSelectQuestionClass}
      data-name="Single-select question"
    >
      <div className={`${containerClass}`}>
        {renderContent()}
        <div
          aria-live="polite"
          role="status"
          key={`prediction-feedback-${resetCount}`}
        >
          {showPredictionFeedback && (
            <FeedbackBanner
              variant="information"
              message={messages.predictionSubmitted}
              className={predictionFeedbackWrapperClass}
            />
          )}
        </div>
        <QuestionFooter
          attemptsLeft={attemptsLeft}
          showReset={showResetButton}
          onSubmit={handleSubmit}
          onReset={handleReset}
          isSubmitDisabled={isSubmitDisabled}
          isPredictionQuestion={isPredictionQuestion}
        />
      </div>
    </div>
  );
};

export const SingleSelectQuestion = (props: SingleSelectQuestionProps) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <SingleSelectQuestionInternal {...props} />
  </TranslationsProvider>
);
